import { useState, useEffect } from 'react';
import { Save, FileText, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CGVEditor({ token }) {
  const [content, setContent] = useState('');
  const [original, setOriginal] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/admin/cgv`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setContent(data.content || '');
          setOriginal(data.content || '');
        }
      } catch { toast.error('Erreur chargement CGV'); }
      finally { setLoading(false); }
    })();
  }, [token]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/cgv`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ content })
      });
      if (!res.ok) throw new Error();
      setOriginal(content);
      toast.success('CGV sauvegardées');
    } catch { toast.error('Erreur sauvegarde'); }
    finally { setSaving(false); }
  };

  const hasChanges = content !== original;

  return (
    <div className="space-y-4" data-testid="cgv-editor">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[#0a57a4] flex items-center gap-2">
          <FileText className="w-4 h-4" /> Conditions Générales de Vente
        </h3>
        <div className="flex items-center gap-2">
          {hasChanges && (
            <Button variant="outline" size="sm" onClick={() => setContent(original)} className="text-xs">
              <RotateCcw className="w-3 h-3 mr-1" /> Annuler
            </Button>
          )}
          <Button size="sm" onClick={save} disabled={saving || !hasChanges}
            className="bg-[#0a57a4] hover:bg-[#099dbb] text-white" data-testid="save-cgv-btn">
            <Save className="w-3 h-3 mr-1" /> {saving ? 'Sauvegarde...' : 'Sauvegarder'}
          </Button>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 text-xs text-blue-800">
        Ce texte sera converti en PDF et envoyé en pièce jointe avec l'état des lieux. Chaque ligne vide crée un espacement. Les lignes commençant par "Article" seront en gras.
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#858275]">Chargement...</div>
      ) : (
        <Textarea
          value={content}
          onChange={e => setContent(e.target.value)}
          rows={25}
          className="text-sm font-mono resize-none leading-relaxed"
          placeholder="Saisissez les conditions générales de vente..."
          data-testid="cgv-content-textarea"
        />
      )}
    </div>
  );
}
